import React, { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { COMPANY } from '../content';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  { value: 'kitchen', label: 'Kitchen Renovation' },
  { value: 'bathroom', label: 'Bathroom / Spa Suite' },
  { value: 'carpentry', label: 'Custom Carpentry / Built-Ins' },
  { value: 'basement', label: 'Basement Renovation' },
  { value: 'addition', label: 'Whole-Home / Addition' },
  { value: 'other', label: 'Something Else' },
];

const BUDGETS = [
  { value: 'under_30k', label: 'Under $30,000' },
  { value: '30k_60k', label: '$30,000 – $60,000' },
  { value: '60k_100k', label: '$60,000 – $100,000' },
  { value: '100k_150k', label: '$100,000 – $150,000' },
  { value: '150k_plus', label: '$150,000+' },
];

export default function GreenlineContact() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', state: 'MA', zip: '',
    project_type: 'kitchen', budget: '60k_100k', timeline: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Name, email and phone are required');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND}/api/public/lead`, form);
      if (res.data?.ok) {
        setDone(true);
        toast.success("Thanks — we'll be in touch within one business day.");
      } else {
        toast.error('Submission failed — please call us instead.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Submission failed');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="pt-40 pb-40 min-h-[80vh] flex items-center">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <CheckCircle2 className="w-20 h-20 mx-auto" style={{ color: 'var(--gl-primary)' }} />
          <div className="gl-overline mt-8 mb-4">Received</div>
          <h1 className="gl-serif text-white text-5xl sm:text-7xl leading-none mb-6">Thank you.</h1>
          <p className="text-base text-[color:var(--gl-muted)] leading-relaxed max-w-md mx-auto mb-10">
            We&apos;ll review your request and reach out within one business day to schedule a no-pressure consultation. In the meantime, feel free to call.
          </p>
          <a href={COMPANY.phoneHref} className="gl-btn-primary inline-flex">{COMPANY.phone}</a>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="pt-40 pb-16 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="gl-overline mb-6 gl-reveal"><span className="gl-hairline" />Start a Conversation</div>
          <h1 className="gl-serif text-white text-5xl sm:text-7xl lg:text-[8rem] leading-[0.92] gl-reveal" style={{ transitionDelay: '0.05s' }}>
            Tell us about <em className="not-italic" style={{ color: 'var(--gl-primary)' }}>your project.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg text-[color:var(--gl-fg)]/80 leading-relaxed gl-reveal" style={{ transitionDelay: '0.12s' }}>
            The fastest path to a quote is the form below. Every detail you can share — even a rough Pinterest mood — helps us bring real numbers to the first meeting.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-3 gap-16">
          <aside className="space-y-12">
            <div className="gl-reveal">
              <div className="gl-overline mb-3">Call</div>
              <a href={COMPANY.phoneHref} className="gl-serif text-3xl text-white block hover:text-[color:var(--gl-primary)] transition-colors">{COMPANY.phone}</a>
              <div className="text-xs text-[color:var(--gl-muted)] mt-2 tracking-wide">Mon–Fri · 8am–6pm ET</div>
            </div>
            <div className="gl-reveal">
              <div className="gl-overline mb-3">Email</div>
              <a href={COMPANY.emailHref} className="gl-serif text-2xl text-white block hover:text-[color:var(--gl-primary)] transition-colors break-all">{COMPANY.email}</a>
            </div>
            <div className="gl-reveal">
              <div className="gl-overline mb-3">Service Area</div>
              <p className="text-white leading-relaxed">{COMPANY.address}</p>
              <p className="text-xs text-[color:var(--gl-muted)] mt-2">Outside the area? Email us — we travel for the right project.</p>
            </div>
          </aside>

          <form onSubmit={submit} className="lg:col-span-2 space-y-6 gl-reveal">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="gl-label">Full Name *</label><input className="gl-input" value={form.name} onChange={update('name')} required data-testid="lead-form-name" /></div>
              <div><label className="gl-label">Phone *</label><input className="gl-input" value={form.phone} onChange={update('phone')} required type="tel" data-testid="lead-form-phone" /></div>
              <div className="sm:col-span-2"><label className="gl-label">Email *</label><input className="gl-input" value={form.email} onChange={update('email')} required type="email" data-testid="lead-form-email" /></div>
              <div><label className="gl-label">Address</label><input className="gl-input" value={form.address} onChange={update('address')} data-testid="lead-form-address" /></div>
              <div><label className="gl-label">City</label><input className="gl-input" value={form.city} onChange={update('city')} data-testid="lead-form-city" /></div>
              <div><label className="gl-label">State</label><input className="gl-input" value={form.state} onChange={update('state')} maxLength={3} data-testid="lead-form-state" /></div>
              <div><label className="gl-label">ZIP</label><input className="gl-input" value={form.zip} onChange={update('zip')} data-testid="lead-form-zip" /></div>
              <div>
                <label className="gl-label">Project Type *</label>
                <select className="gl-select" value={form.project_type} onChange={update('project_type')} data-testid="lead-form-project-type">
                  {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="gl-label">Approx. Budget</label>
                <select className="gl-select" value={form.budget} onChange={update('budget')} data-testid="lead-form-budget">
                  {BUDGETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2"><label className="gl-label">Target Timeline</label><input className="gl-input" value={form.timeline} onChange={update('timeline')} placeholder="e.g. start in spring, finish by Thanksgiving" data-testid="lead-form-timeline" /></div>
              <div className="sm:col-span-2"><label className="gl-label">Tell us about the project</label><textarea className="gl-textarea" rows={6} value={form.notes} onChange={update('notes')} placeholder="What you're hoping for, must-haves, deal-breakers, links to inspiration…" data-testid="lead-form-notes" /></div>
            </div>
            <button type="submit" disabled={busy} className="gl-btn-primary w-full sm:w-auto" data-testid="lead-form-submit">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send Request →</>}
            </button>
            <p className="text-xs text-[color:var(--gl-muted)] mt-3">Your information is sent directly to Greenline Construction. We won&apos;t share or sell it.</p>
          </form>
        </div>
      </section>
    </>
  );
}
